import { Injectable } from "@angular/core";
import { Observable, Subject, interval, EMPTY } from "rxjs";
import {
  switchMap,
  distinctUntilChanged,
  map,
  catchError,
  takeUntil,
  filter,
} from "rxjs/operators";
import { ScheduledOrderService } from "./scheduled-order.service";
import { ScheduledOrder } from "../../../core/models/scheduled-order.interface";
import { QPilotAccessToken } from "../../../core/services/qpilot.service";

/**
 * Service for real-time scheduled order data updates
 * Implements basic polling (every 10 seconds) for orders in active processing states
 *
 * IMPORTANT: Should only be used for orders with status "Processing" or "Queued"
 * to avoid unnecessary API load. For stable states (Active, Paused, etc.),
 * polling is not needed.
 *
 * Future enhancement: Can be replaced with WebSocket for true real-time updates
 */
@Injectable({ providedIn: "root" })
export class ScheduledOrderLiveDataService {
  private pollingInterval = 10000; // 10 seconds
  private destroy$ = new Subject<void>();
  private currentOrderId?: number;

  constructor(private scheduledOrderService: ScheduledOrderService) {}

  /**
   * Subscribe to live updates for a scheduled order
   * Polls the API every 10 seconds to check for status changes
   *
   * @param siteId Site ID
   * @param scheduledOrderId Scheduled order ID
   * @param accessToken Optional access token for embedded mode
   * @returns Observable that emits the order whenever its status changes
   */
  subscribe(
    siteId: number,
    scheduledOrderId: number,
    accessToken?: QPilotAccessToken,
  ): Observable<ScheduledOrder> {
    this.currentOrderId = scheduledOrderId;

    return interval(this.pollingInterval).pipe(
      switchMap(() =>
        this.scheduledOrderService.getById(scheduledOrderId, accessToken).pipe(
          catchError((error) => {
            console.error("Live data polling error:", error);
            return EMPTY; // Continue polling even if one request fails
          }),
        ),
      ),
      filter((order): order is ScheduledOrder => !!order), // Type guard
      map((order) => ({
        ...order,
        Status: order.Status || "Unknown",
      })),
      distinctUntilChanged(
        (prev, curr) =>
          prev.Status === curr.Status && prev.UpdatedUtc === curr.UpdatedUtc,
      ),
      takeUntil(this.destroy$),
    );
  }

  /**
   * Unsubscribe from live updates
   * Call this in ngOnDestroy to stop polling
   */
  unsubscribe(): void {
    this.destroy$.next();
    this.currentOrderId = undefined;
  }

  /**
   * Set custom polling interval (for testing or customization)
   * @param milliseconds Polling interval in milliseconds
   */
  setPollingInterval(milliseconds: number): void {
    this.pollingInterval = milliseconds;
  }

  /**
   * Get the current order ID being monitored
   */
  getCurrentOrderId(): number | undefined {
    return this.currentOrderId;
  }

  /**
   * Check if currently subscribed to updates
   */
  isSubscribed(): boolean {
    return this.currentOrderId !== undefined;
  }
}
